// src/components/appointment/AppointmentEditForm.js
import { FaTimes } from 'react-icons/fa';
import {formatBloodGroup, parseBloodGroup } from '../../../api/config'
import PageHeading from '../../PageHeading';
import DynamicSelect from '../../DynamicSelect';

/**
 * Appointment Edit Form Component
 * @param {Object} props - Component props
 * @param {Object} props.editData - Current appointment data being edited
 * @param {Function} props.handleEditChange - Function to handle form field changes
 * @param {Function} props.handleSaveEdit - Function to save the edited appointment
 * @param {string} props.editMode - ID of the appointment being edited
 * @param {Function} props.onCancel - Function to cancel editing
 */
const AppointmentEditForm = ({ 
  editData, 
  handleEditChange, 
  handleSaveEdit, 
  editMode, 
  onCancel 
}) => {

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Appointments"},
    { label: "Edit Appointment" },
  ];

    // Blood Groups options for DynamicSelect
    const bloodGroupOptions = [
    { value: "A+", label: "A+" },
    { value: "A-", label: "A-" },
    { value: "B+", label: "B+" },
    { value: "B-", label: "B-" },
    { value: "O+", label: "O+" },
    { value: "O-", label: "O-" },
    { value: "AB+", label: "AB+" },
    { value: "AB-", label: "AB-" },
  ];

    // Payment Methods options for DynamicSelect
    const paymentMethodOptions = [
      { value: "CASH", label: "Cash" },
      { value: "BKASH", label: "Bkash" },
      { value: "NAGAD", label: "Nagad" },
      { value: "ROCKET", label: "Rocket" },
      { value: "BANK", label: "Bank" },
    ];

  return (
    <div>
        <div>
          <PageHeading title="Edit Appointment" breadcrumbs={breadcrumbs}/>
        </div>
         <div className="flex justify-end mb-4 ">
           <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 px-6">
             <FaTimes size={18} />
           </button>
         </div>
      <div className="bg-white shadow-sm rounded-lg">
      <div className="flex items-center justify-start border-b border-dashed border-M-text-color/50 p-4">
         <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
           Basic Information
         </h2>
       </div>
       <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
         {/* Doctor Name */}
         <div>
           <label className="label">Doctor Name</label>
           <input
             type="text"
             name="doctorName"
             className="input-field"
             value={editData.doctorName}
             onChange={handleEditChange}
           />
         </div>
         
         {/* Patient Name */}
         <div>
           <label className="label">Patient Name</label>
           <input
             type="text"
             name="patientName"
             className="input-field"
             value={editData.patientName}
             onChange={handleEditChange}
           />
         </div>
         
         {/* Mobile Number */}
         <div>
           <label className="label">Mobile Number</label>
           <input
             type="text"
             name="mobileNumber"
             className="input-field"
             value={editData.mobileNumber}
             onChange={handleEditChange}
           />
         </div>
         
         {/* Serial Number */}
         <div>
           <label className="label">Serial Number</label>
           <input
             type="text"
             name="serialNumber"
             className="input-field"
             value={editData.serialNumber}
             onChange={handleEditChange}
           />
         </div>
         
         {/* Weight */}
         <div>
           <label className="label">Weight (kg)</label>
           <input
             type="number"
             name="weight"
             className="input-field"
             value={editData.weight}
             onChange={handleEditChange}
           />
         </div>
         
         {/* Age */}
         <div>
           <label className="label">Age (years)</label>
           <input
             type="number"
             name="age"
             className="input-field"
             value={editData.age}
             onChange={handleEditChange}
           />
         </div>
         
         {/* Blood Group */}
           <div>
             <label className="label">Blood Group</label>
             <DynamicSelect
               options={bloodGroupOptions}
               onChange={(selectedOption) => handleEditChange({ target: { name: 'bloodGroup', value: selectedOption ? selectedOption.value : '' } })}
               value={bloodGroupOptions.find(option => option.value === editData.bloodGroup)}
               isSearchable
             />
           </div>
         
         {/* Consultation Fee */}
         <div>
           <label className="label">Consultation Fee</label>
           <input
             type="number"
             name="consultationFee"
             className="input-field"
             value={editData.consultationFee}
             onChange={handleEditChange}
           />
         </div>
         
         {/* Payment Method */}
         <div>
             <label className="label">Payment Method</label>
             <DynamicSelect
               options={paymentMethodOptions}
               onChange={(selectedOption) => handleEditChange({ target: { name: 'paymentMethod', value: selectedOption ? selectedOption.value : '' } })}
               value={paymentMethodOptions.find(option => option.value === editData.paymentMethod)}
               isSearchable
             />
           </div>
         
         {/* Address */}
         <div>
           <label className="label">Address</label>
           <input
             type="text"
             name="address"
             className="input-field"
             value={editData.address}
             onChange={handleEditChange}
           />
         </div>
         
         {/* Reason */}
         <div className="md:col-span-2">
           <label className="label">Reason</label>
           <textarea
             name="reason"
             className="input-field"
             rows={3}
             value={editData.reason}
             onChange={handleEditChange}
           ></textarea>
         </div>
       </div>
      </div>
        {/* Save and Cancel Buttons */}
         <div className="mt-6 flex gap-4 py-5">
              <button
                onClick={() => handleSaveEdit(editMode)}
                className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              >
                Save Changes
              </button>
              <button
                onClick={onCancel}
                className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
              >
                Cancel
              </button>
            </div>
    </div>
  );
};

/**
 * Function to prepare appointment data for editing
 * @param {Object} appointment - Original appointment data
 * @returns {Object} - Formatted data for editing
 */
export const prepareAppointmentForEdit = (appointment) => {
  let formattedBloodGroup = formatBloodGroup(appointment.bloodGroup || "");
  
  return {
    doctorName: appointment.doctorName || "",
    patientName: appointment.patientName || "",
    mobileNumber: appointment.mobileNumber || "",
    serialNumber: appointment.serialNumber || "",
    weight: appointment.weight || "",
    age: appointment.age || "",
    bloodGroup: formattedBloodGroup,
    consultationFee: appointment.consultationFee || "",
    paymentMethod: appointment.paymentMethod || "",
    reason: appointment.reason || "",
    address: appointment.address || ""
  };
};

/**
 * Function to prepare edited data for API submission
 * @param {Object} editData - Edited appointment data
 * @returns {Object} - Formatted data for API
 */
export const prepareEditDataForApi = (editData) => {
  return {
    ...editData,
    bloodGroup: editData.bloodGroup ? parseBloodGroup(editData.bloodGroup) : editData.bloodGroup,
    weight: editData.weight ? parseFloat(editData.weight) : null,
    age: editData.age ? parseInt(editData.age) : null,
    consultationFee: editData.consultationFee ? parseFloat(editData.consultationFee) : null
  };
};

export default AppointmentEditForm;